import { useContext, useEffect, useRef, useState, type MutableRefObject } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router";
import { Dialog } from "../../../components/ui";
import { t } from "../../../lib/sprache";

/**
 * Ungespeicherte Änderungen im Vertrags- und Vorlagen-Editor (Befund 20): Wechsel innerhalb der App fragt nach,
 * Schließen oder Neuladen des Tabs warnt der Browser. `allow()` vor einer gewollten Navigation (nach dem Speichern).
 */
export function useUngespeichert(dirty: boolean) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirtyRef.current && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  return { blocker, allow: () => { dirtyRef.current = false; } };
}

/**
 * Rückfrage beim Verlassen mit ungespeicherten Änderungen. Mit `onSave` gibt es zusätzlich „Speichern und verlassen“
 * (Praxistest K-005): Wer einem Link ins Creator-Profil oder in die Kooperation folgt, verliert seine Eingaben nicht.
 */
export function VerwerfenDialog({ blocker, onSave }: { blocker: ReturnType<typeof useBlocker>; onSave?: () => Promise<boolean> }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(false);
  const speichern = async () => {
    if (!onSave || busy) return;
    setBusy(true);
    setFehler(false);
    try {
      if (await onSave()) blocker.proceed?.();
      else setFehler(true);
    } catch {
      setFehler(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={blocker.state === "blocked"} onClose={() => blocker.reset?.()} title={onSave ? t("Änderungen speichern?", "Save changes?") : t("Änderungen verwerfen?", "Discard changes?")}>
      <p>{t("Du hast Änderungen, die noch nicht gespeichert sind. Beim Verlassen gehen sie verloren.", "You have changes that have not been saved yet. They will be lost when you leave.")}</p>
      {fehler && <p className="tk-error" role="alert">{t("Speichern hat nicht geklappt. Bitte die Meldung im Formular prüfen.", "Saving did not work. Please check the message in the form.")}</p>}
      <div className="row">
        <button type="button" className={onSave ? undefined : "primary"} disabled={busy} onClick={() => blocker.reset?.()}>{t("Weiter bearbeiten", "Continue editing")}</button>
        {onSave && <button type="button" className="primary" disabled={busy} aria-busy={busy} onClick={() => void speichern()}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>}
        <button type="button" className="danger" disabled={busy} onClick={() => blocker.proceed?.()}>{t("Verwerfen", "Discard")}</button>
      </div>
    </Dialog>
  );
}

/**
 * Wächter als eigenes Bauteil (Praxistest K-106): für Seiten, die auch ohne den Router der App gezeichnet werden (etwa in
 * Tests). `useBlocker` gibt es nur im Router der App; die Seite fragt mit `useDatenRouter()` nach und zeichnet den Wächter nur
 * dann. `allowRef.current()` vor einer gewollten Navigation (nach dem Speichern).
 */
export function UngespeichertWaechter({ dirty, allowRef }: { dirty: boolean; allowRef: MutableRefObject<() => void> }) {
  const { blocker, allow } = useUngespeichert(dirty);
  allowRef.current = allow;
  return <VerwerfenDialog blocker={blocker} />;
}
export function useDatenRouter(): boolean {
  return useContext(UNSAFE_DataRouterContext) != null;
}
